import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from 'utils/style/theme';
import { Upload, message, Image } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import cloudIcon from 'utils/icon/icon_cloud-upload-image-black.svg';
import uploadIcon from 'utils/icon/icon_upload-image-black.svg';
import plusButtonIcon from 'utils/icon/icon_plus-blue.svg';
import PrimaryButton from 'components/button/PrimaryButton';
import { useTranslation } from 'react-i18next';
import { backgroundApi } from 'apis/resource/backgroundApis';

/**
 * * 단일 이미지 upload Component.
 */

interface IProps {
  data: any;
  setData: React.Dispatch<React.SetStateAction<any>>;
  hideRequired?: boolean;
}

const UploadImage: React.FC<IProps> = ({ data, setData, hideRequired }) => {
  const [image, setImage] = useState<string | null>(null);
  useEffect(() => {
    if (data) {
      setImage(data?.image_url);
    }
  }, [data]);

  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState<boolean>(false);
  const handleMessage = (type, content) => {
    messageApi.open({
      type,
      content,
    });
  };
  const uploadAvatar = async (options) => {
    const { file, onSuccess, onError } = options;
    const isPNG = file.type === 'image/png';
    const isJPG = file.type === 'image/jpeg';

    if (isPNG || isJPG) {
      if (file.size > 8000000) {
        messageApi.error('Upload failed. Max size for files is 8 MB.');
      } else {
        if (data) {
          try {
            setLoading(true);
            const res = await backgroundApi.backgroundUpdateImage(
              data.project_id,
              data.id,
              file,
            );
            const cloneData = JSON.parse(JSON.stringify(data));
            cloneData['image_url'] = res.image_url;
            cloneData['image_key'] = res.image_key;
            cloneData['bucket'] = res.bucket;

            setData(cloneData);
            onSuccess('ok');
            handleMessage(
              'success',
              '이미지 변경이 완료되었습니다. (이미지는 교체시 즉시 적용됩니다)',
            );
            setLoading(false);
          } catch (err) {
            console.log('Error', err);
            handleMessage('error', `${file.name} upload failed`);
            onError(err);
            setLoading(false);
          }
        }
      }
    } else {
      handleMessage('error', `${file.name} : 올바른 이미지 포맷이 아닙니다`);
    }
  };
  const props = {
    name: 'file',
    action: '',
    showUploadList: false,
    async onChange(info) {
      if (info.file.status !== 'uploading') {
        // console.log('uploading ===>', info.file, info.fileList);
      }
      if (info.file.status === 'done') {
      } else if (info.file.status === 'error') {
        console.log('failed ===>', info.file);
      }
    },
  };

  return (
    <>
      <ImageSection>
        <p className='title'>** 이미지는 교체시 즉시 적용됩니다</p>
        <br></br>
        <UploadWrapper>
          <UploadContentWrapper>
            <div>
              {loading ? (
                <div className='flex-wrap icon'>
                  <LoadingOutlined style={{ fontSize: '50px' }} />
                </div>
              ) : image ? (
                <Image src={image} alt={`thumbnail`} className='icon' />
              ) : (
                <></>
              )}
            </div>
          </UploadContentWrapper>
          {!loading && (
            <div className='uploadTotalWrap'>
              <Upload {...props} maxCount={1} customRequest={uploadAvatar}>
                {image ? (
                  <div style={{ marginLeft: 24 }}>
                    <PrimaryButton
                      text={t('buttons.replaceImage')}
                      height='40px'
                      width='fit-content'
                      bgColor='#ffffff'
                      textColor={theme.colors.dark_400}
                      fontWeight={theme.fontWeights.medium}
                      fontSize={theme.fontSizes.normal}
                      border={`1px solid ${theme.colors.gray_6}`}
                      rounded='6px'
                      hoverBgColor={theme.colors.gray_6}
                      activeBgColor={theme.colors.gray_6}
                      selectedBgColor={theme.colors.gray_6}
                      loading={loading}
                      LeftPlaceHolder={<img src={uploadIcon} alt='upload' />}
                    />
                    <p className='turText'>{t('updateStory.form.required')}</p>
                  </div>
                ) : (
                  <>
                    <div className='emptyImage'>
                      <img src={cloudIcon} alt='upload cloud' />
                      <p className='emptyImageText'>
                        {t('updateStory.form.placeholder')}
                      </p>
                      <img src={plusButtonIcon} alt='plus icon' />
                    </div>
                    <p className='turText'>{t('updateStory.form.required')}</p>
                  </>
                )}
              </Upload>
            </div>
          )}
        </UploadWrapper>
      </ImageSection>
      {!hideRequired && (
        <ul>
          <li className='turText'>
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.miniCutContent.minicutRegisterModal.requiredDuplicate',
            )}
          </li>
          <li className='turText'>{t('updateStory.form.required')}</li>
        </ul>
      )}
      {contextHolder}
    </>
  );
};

export default UploadImage;

const UploadContentWrapper = styled.div`
  .upload {
    border: 1px solid ${theme.colors.gray_7};
    border-style: dotted;
  }
  .flex-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }
  .icon {
    width: 136px;
    height: 136px;
    object-fit: cover;
  }
`;

const UploadWrapper = styled.div`
  position: relative;
  display: flex;
  .emptyImage {
    border: 1px solid ${theme.colors.dark_100};
    border-radius: 4px;
    border-style: dotted;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 24px;
    & > * + * {
      margin-top: 10px;
    }
    cursor: pointer;
  }
  .turText {
    font-size: ${theme.fontSizes.sm};
    font-weight: ${theme.fontWeights.normal};
    line-height: 20px;
    letter-spacing: 0.2px;
    margin-top: 8px;
  }
  .emptyImageText {
    color: ${theme.colors.dark_500};
    font-size: ${theme.fontSizes.normal};
    font-weight: ${theme.fontWeights.normal};
    line-height: 24px;
    letter-spacing: 0.2px;
  }
  .uploadTotalWrap {
    display: flex;
    & > * + * {
      margin-left: 8px;
    }

    width: 100%;
  }
`;

const ImageSection = styled.div`
  padding: 16px;
  background-color: ${theme.colors.blue_50};
  border-radius: 8px;
`;
